import { useEffect, useState } from "react";

import { crearProyecto } from "../api/client";
import {
  FORMATOS_FLYER,
  type FormatoFlyer,
  type Proyecto,
  type TextoLiteral,
} from "../api/types";
import { useModelos } from "../hooks/useModelos";
import { EditorTextoLiteral } from "./EditorTextoLiteral";
import estilos from "./FormularioNuevoProyecto.module.css";

interface Props {
  token: string;
  onCreado: (proyecto: Proyecto) => void;
}

const LIMITE_BRIEF = 4000;

export function FormularioNuevoProyecto({
  token,
  onCreado,
}: Props): JSX.Element {
  const [brief, setBrief] = useState("");
  const [formato, setFormato] = useState<FormatoFlyer>("1:1");
  const [modelo, setModelo] = useState("");
  const [effort, setEffort] = useState("");
  const [presupuesto, setPresupuesto] = useState("");
  const [textoLiteral, setTextoLiteral] = useState<TextoLiteral[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { catalogo, cargando, error: errorCatalogo, recargar } = useModelos();

  // El modelo/effort SIEMPRE se eligen en la UI (requisito vinculante): en
  // cuanto llega el catalogo, precargamos el primer alias de cada lista para
  // que el select nunca quede vacio.
  useEffect(() => {
    if (catalogo && !modelo && catalogo.models.length > 0) {
      setModelo(catalogo.models[0]);
    }
    if (catalogo && !effort && catalogo.efforts.length > 0) {
      setEffort(catalogo.efforts[0]);
    }
  }, [catalogo, modelo, effort]);

  const enviar = async (
    evento: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    evento.preventDefault();
    if (enviando) return;

    const briefLimpio = brief.trim();
    if (!briefLimpio) {
      setError("Describe que anuncia el flyer.");
      return;
    }
    if (!modelo || !effort) {
      setError("Elige modelo y esfuerzo (o espera a que cargue el catalogo).");
      return;
    }

    const presupuestoNum = presupuesto.trim() ? Number(presupuesto) : null;
    if (
      presupuestoNum !== null &&
      (Number.isNaN(presupuestoNum) || presupuestoNum <= 0)
    ) {
      setError("El presupuesto debe ser un numero mayor a 0.");
      return;
    }

    const literalesLimpios = textoLiteral
      .map((t) => ({
        valor: t.valor.trim(),
        etiqueta: t.etiqueta?.trim() || undefined,
      }))
      .filter((t) => t.valor.length > 0);

    setEnviando(true);
    setError(null);
    try {
      const proyecto = await crearProyecto(token, {
        brief: briefLimpio,
        format: formato,
        model: modelo,
        effort,
        ...(presupuestoNum !== null ? { budget_max_usd: presupuestoNum } : {}),
        ...(literalesLimpios.length > 0
          ? { texto_usuario: literalesLimpios }
          : {}),
      });
      onCreado(proyecto);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error creando el proyecto",
      );
      setEnviando(false);
    }
  };

  const catalogoDisponible = Boolean(catalogo) && !cargando;

  return (
    <section className={estilos.tarjeta} aria-labelledby="titulo-nuevo">
      <header className={estilos.encabezado}>
        <h2 id="titulo-nuevo">Nuevo flyer</h2>
        <p>
          Describe que anuncia, el texto exacto que quieres incluir y la marca;
          la IA disena el flyer desde cero.
        </p>
      </header>

      {error && (
        <div
          className={estilos.error}
          role="alert"
          style={{ marginBottom: 16 }}
        >
          {error}
        </div>
      )}

      <form onSubmit={enviar} className={estilos.grid} noValidate>
        <div>
          <label htmlFor="brief">Brief</label>
          <textarea
            id="brief"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            maxLength={LIMITE_BRIEF}
            required
            disabled={enviando}
            placeholder="Ej: Promo de cafe 2x1 los sabados en Cafeteria Aurora. Telefono: 555-123-4567. Tono calido y cercano."
          />
          <div className={estilos.contador}>
            {brief.length} / {LIMITE_BRIEF}
          </div>
        </div>

        <fieldset className={estilos.grupoTextoLiteral}>
          <legend>Texto literal (opcional)</legend>
          <p className={estilos.descripcion}>
            Precios, telefonos, fechas o terminos que deben salir EXACTOS; el
            resto del copy lo redacta la IA.
          </p>
          <EditorTextoLiteral
            idBase="nuevo-proyecto-literal"
            valores={textoLiteral}
            onCambiar={setTextoLiteral}
            deshabilitado={enviando}
          />
        </fieldset>

        <div className={estilos.dosColumnas}>
          <div>
            <label htmlFor="formato">Formato</label>
            <select
              id="formato"
              value={formato}
              onChange={(e) => setFormato(e.target.value as FormatoFlyer)}
              disabled={enviando}
            >
              {FORMATOS_FLYER.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="presupuesto">Presupuesto maximo (USD)</label>
            <input
              id="presupuesto"
              type="number"
              min="0.01"
              step="0.01"
              value={presupuesto}
              onChange={(e) => setPresupuesto(e.target.value)}
              placeholder="0.50 (por defecto)"
              disabled={enviando}
            />
          </div>
        </div>

        {errorCatalogo && (
          <div className={estilos.nota} role="status">
            {errorCatalogo}{" "}
            <button
              type="button"
              className="boton boton-secundario"
              onClick={recargar}
              style={{ marginLeft: 8 }}
            >
              Reintentar
            </button>
          </div>
        )}

        <div className={estilos.dosColumnas}>
          <div>
            <label htmlFor="modelo">Modelo</label>
            <select
              id="modelo"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
              disabled={enviando || !catalogoDisponible}
            >
              {(catalogo?.models ?? []).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <p className={estilos.descripcion}>
              Modelo de Claude que disena el flyer (director + revisor).
            </p>
          </div>

          <div>
            <label htmlFor="effort">Esfuerzo</label>
            <select
              id="effort"
              value={effort}
              onChange={(e) => setEffort(e.target.value)}
              disabled={enviando || !catalogoDisponible}
            >
              {(catalogo?.efforts ?? []).map((ef) => (
                <option key={ef} value={ef}>
                  {ef}
                </option>
              ))}
            </select>
            <p className={estilos.descripcion}>
              Mayor esfuerzo = mas iteraciones internas, mejor calidad, mas
              costo/tiempo.
            </p>
          </div>
        </div>

        <div className={estilos.pie}>
          <button
            type="submit"
            className="boton boton-primario boton-grande"
            disabled={enviando}
          >
            {enviando ? "Creando..." : "Crear proyecto"}
          </button>
        </div>
      </form>
    </section>
  );
}
